/**
 * UUIDv7 generator — 48-bit big-endian ms timestamp + 74 random bits + version/variant bits.
 * Client-generated. Used as `events.id` — this IS the idempotency key (DEC-003 / DEC-012).
 * Retries of the same logical action MUST reuse the same UUIDv7 so `PRIMARY KEY (id)`
 * no-ops the duplicate on the server.
 */
export function uuidv7(): string {
  const ms = BigInt(Date.now())
  const bytes = new Uint8Array(16)

  // 48-bit unix milliseconds, big-endian, bytes 0..5
  bytes[0] = Number((ms >> 40n) & 0xffn)
  bytes[1] = Number((ms >> 32n) & 0xffn)
  bytes[2] = Number((ms >> 24n) & 0xffn)
  bytes[3] = Number((ms >> 16n) & 0xffn)
  bytes[4] = Number((ms >> 8n) & 0xffn)
  bytes[5] = Number(ms & 0xffn)

  // 10 random bytes in bytes 6..15
  crypto.getRandomValues(bytes.subarray(6, 16))

  // Version 7 in high nibble of byte 6
  bytes[6] = (bytes[6] & 0x0f) | 0x70
  // RFC 4122 variant in top two bits of byte 8
  bytes[8] = (bytes[8] & 0x3f) | 0x80

  // Render to canonical 8-4-4-4-12 hex string
  const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('')
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`
}

/** Extract ms-since-epoch encoded in a UUIDv7. Returns null if the UUID isn't v7. */
export function timestampOf(uuid: string): Date | null {
  const clean = uuid.replace(/-/g, '')
  if (clean.length !== 32) return null
  const versionNibble = parseInt(clean.charAt(12), 16)
  if (versionNibble !== 7) return null

  const msHex = clean.slice(0, 12)
  const ms = Number(BigInt(`0x${msHex}`))
  return new Date(ms)
}
